import type { Maybe } from "@/domains/common/types";
import type { Uuid } from "@/domains/global/identifiers";
import { AssistantProjectObservable } from "@/store/assistant/AssistantProjectObservable";
import type { AssistantProjectIndexData, AssistantProjectModelData } from "@/store/assistant/types";
import type { AppStore } from "@/store/AppStore";
import { BaseSyncModelStore } from "@/store/sync/BaseSyncModelStore";
import { OptimisticSyncUpdate, SyncModelKind, SyncUpdateValue } from "@/store/sync/types";
import { AppSubStoreArgs } from "@/store/types";
import { makeObservable, override } from "mobx";

export class AppStoreAssistantProjectsStore extends BaseSyncModelStore<
  AssistantProjectObservable,
  AssistantProjectModelData
> {
  constructor(injectedDeps: AppSubStoreArgs) {
    super({ modelKind: SyncModelKind.AssistantProject, ...injectedDeps });
    makeObservable<this, "createSyncModel">(this, {
      createSyncModel: false,
      computeIndexes: override,
      getAssistantProjectObservableById: false,
    });
  }

  createSyncModel(data: SyncUpdateValue<AssistantProjectModelData>): AssistantProjectObservable {
    return new AssistantProjectObservable({
      id: data.model_id,
      data,
      store: this.store,
    });
  }

  public computeIndexes({
    remoteData,
    optimisticUpdates,
  }: {
    store: AppStore;
    remoteData: Maybe<SyncUpdateValue<AssistantProjectModelData>>;
    optimisticUpdates: OptimisticSyncUpdate<AssistantProjectModelData>[];
  }): AssistantProjectIndexData {
    const syncUpdateValue =
      (optimisticUpdates.at(-1)?.value as SyncUpdateValue<AssistantProjectModelData> | undefined) ||
      remoteData;
    const modelData = syncUpdateValue?.model_data;

    return {
      assistant_id: modelData?.assistant_id ?? "",
      status: modelData?.status ?? "",
      last_activity_at: modelData?.last_activity_at ?? "",
      created_at: modelData?.created_at ?? "",
      updated_at: modelData?.updated_at ?? "",
    };
  }

  getAssistantProjectObservableById({
    assistantProjectId,
  }: {
    assistantProjectId: Uuid;
  }): AssistantProjectObservable | undefined {
    return this.get(assistantProjectId);
  }
}
